/**
 * API client para o app `whatsapp/` do backend (Fase F1-F6 + ADR-0034).
 *
 * Endpoints:
 *   /api/whatsapp/instancias                CRUD + actions (iniciar-sessao, pausar, retomar, desconectar)
 *   /api/whatsapp/conversas                 read-only + actions (fechar, reabrir, marcar-lida)
 *   /api/whatsapp/conversas/{id}/mensagens  list (?since=) + create (raw_text + scheduled_at?)
 *   /api/whatsapp/mensagens                 read-only + action cancelar
 *   /api/whatsapp/configuracoes             singleton por unidade
 *
 * Padroes:
 *  - Lista vs Detalhe: list retorna serializer compacto; retrieve traz detalhes
 *    (QR base64 em instancia detail, conteudo completo em conversa detail).
 *  - Polling friendly: ?since=ISO em conversas e mensagens; backend filtra
 *    o que mudou desde o timestamp.
 *  - silentError pode ser passado pelo caller quando a tela trata erro inline.
 */

const PREFIXO_INSTANCIA = "/api/whatsapp/instancias";
const PREFIXO_CONVERSA = "/api/whatsapp/conversas";
const PREFIXO_MENSAGEM = "/api/whatsapp/mensagens";
const PREFIXO_CONFIG = "/api/whatsapp/configuracoes";

// ---------- Instancia ----------

export function listarInstancias(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.unidade) usp.set("unidade", params.unidade);
  if (params.status) usp.set("status", params.status);
  if (params.busca) usp.set("search", params.busca);
  if (params.ordering) usp.set("ordering", params.ordering);
  if (params.page) usp.set("page", params.page);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREFIXO_INSTANCIA}${qs ? `?${qs}` : ""}`);
}

export function obterInstancia(request, id) {
  return request(`${PREFIXO_INSTANCIA}/${id}`);
}

export function criarInstancia(request, dados) {
  return request(PREFIXO_INSTANCIA, {
    method: "POST",
    body: JSON.stringify(dados),
  });
}

export function atualizarInstancia(request, id, dados) {
  return request(`${PREFIXO_INSTANCIA}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

export function excluirInstancia(request, id) {
  return request(`${PREFIXO_INSTANCIA}/${id}`, { method: "DELETE" });
}

// Iniciar sessao no Node service: gera QR (chega via webhook depois de
// ~2s). Cliente faz polling rapido em obterInstancia ate qr_code_atual
// estar disponivel.
export function iniciarSessaoInstancia(request, id) {
  return request(`${PREFIXO_INSTANCIA}/${id}/iniciar-sessao`, { method: "POST" });
}

// Kill switch: bloqueia envio (inbound continua chegando). Motivo
// auditado. Reverte com retomarInstancia.
export function pausarInstancia(request, id, motivo) {
  return request(`${PREFIXO_INSTANCIA}/${id}/pausar`, {
    method: "POST",
    body: JSON.stringify({ motivo }),
  });
}

export function retomarInstancia(request, id) {
  return request(`${PREFIXO_INSTANCIA}/${id}/retomar`, { method: "POST" });
}

// Logout no Node + zera session_data. Permite reescanear QR depois.
export function desconectarInstancia(request, id) {
  return request(`${PREFIXO_INSTANCIA}/${id}/desconectar`, { method: "POST" });
}

// ---------- Conversa ----------

export function listarConversas(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.instance) usp.set("instance", params.instance);
  if (params.status) usp.set("status", params.status);
  if (params.busca) usp.set("search", params.busca);
  if (params.unread !== undefined) usp.set("unread", String(params.unread));
  if (params.since) usp.set("since", params.since);
  if (params.ordering) usp.set("ordering", params.ordering || "-ultima_mensagem_em");
  if (params.page) usp.set("page", params.page);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREFIXO_CONVERSA}${qs ? `?${qs}` : ""}`);
}

export function obterConversa(request, id) {
  return request(`${PREFIXO_CONVERSA}/${id}`);
}

export function fecharConversa(request, id) {
  return request(`${PREFIXO_CONVERSA}/${id}/fechar`, { method: "POST" });
}

export function reabrirConversa(request, id) {
  return request(`${PREFIXO_CONVERSA}/${id}/reabrir`, { method: "POST" });
}

// Zera nao_lidas_count da conversa (idempotente). Chamado automaticamente
// quando usuario abre o chat.
export function marcarConversaLida(request, id) {
  return request(`${PREFIXO_CONVERSA}/${id}/marcar-lida`, { method: "POST" });
}

// ---------- Mensagem ----------

export function listarMensagens(request, conversaId, params = {}) {
  const usp = new URLSearchParams();
  if (params.since) usp.set("since", params.since);
  if (params.limit) usp.set("limit", params.limit);
  if (params.direction) usp.set("direction", params.direction);
  const qs = usp.toString();
  return request(`${PREFIXO_CONVERSA}/${conversaId}/mensagens${qs ? `?${qs}` : ""}`);
}

/**
 * Cria mensagem outbound. Backend monta `final_text` com prefixo `*Nome*`
 * automaticamente (a partir do user logado via request) — frontend so
 * envia o texto puro em `raw_text`.
 *
 *   - Sem `scheduled_at`: envia imediato (chama Node + retorna msg em
 *     status SENDING ou FAILED conforme resultado).
 *   - Com `scheduled_at` ISO: agenda; worker cron dispara quando chegar
 *     a hora.
 */
export function criarMensagem(request, conversaId, { raw_text, scheduled_at }) {
  const body = { raw_text };
  if (scheduled_at) body.scheduled_at = scheduled_at;
  return request(`${PREFIXO_CONVERSA}/${conversaId}/mensagens`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function listarMensagensAgendadas(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.status) usp.set("status", params.status);
  else usp.set("status", "SCHEDULED");
  if (params.instance) usp.set("instance", params.instance);
  if (params.conversation) usp.set("conversation", params.conversation);
  if (params.desde) usp.set("desde", params.desde);
  if (params.ate) usp.set("ate", params.ate);
  if (params.ordering) usp.set("ordering", params.ordering);
  else usp.set("ordering", "scheduled_at");
  if (params.page) usp.set("page", params.page);
  if (params.pageSize) usp.set("page_size", params.pageSize);
  const qs = usp.toString();
  return request(`${PREFIXO_MENSAGEM}${qs ? `?${qs}` : ""}`);
}

// Cancelar mensagem agendada (so funciona em SCHEDULED).
export function cancelarMensagem(request, id) {
  return request(`${PREFIXO_MENSAGEM}/${id}/cancelar`, { method: "POST" });
}

// ---------- Configuracao ----------

// Singleton por unidade — retorna lista com 1 item (ou cria via backend
// automaticamente no primeiro acesso).
export function listarConfiguracoes(request, params = {}) {
  const usp = new URLSearchParams();
  if (params.unidade) usp.set("business_unit", params.unidade);
  const qs = usp.toString();
  return request(`${PREFIXO_CONFIG}${qs ? `?${qs}` : ""}`);
}

export function obterConfiguracao(request, id) {
  return request(`${PREFIXO_CONFIG}/${id}`);
}

export function atualizarConfiguracao(request, id, dados) {
  return request(`${PREFIXO_CONFIG}/${id}`, {
    method: "PATCH",
    body: JSON.stringify(dados),
  });
}

// ---------- Helpers de UI ----------

export const STATUS_INSTANCIA_TONE = {
  DESCONECTADA: "neutral",
  AGUARDANDO_QR: "info",
  CONECTADA: "success",
  EXPIRADA: "warning",
  BANIDA: "danger",
};

export const STATUS_INSTANCIA_LABEL = {
  DESCONECTADA: "Desconectada",
  AGUARDANDO_QR: "Aguardando QR",
  CONECTADA: "Conectada",
  EXPIRADA: "Expirada",
  BANIDA: "Banida",
};

export const STATUS_MENSAGEM_TONE = {
  DRAFT: "neutral",
  SCHEDULED: "warning",
  SENDING: "info",
  SENT: "neutral",
  DELIVERED: "info",
  READ: "success",
  FAILED: "danger",
  CANCELED: "neutral",
  RECEIVED: "neutral",
};

export const STATUS_MENSAGEM_LABEL = {
  DRAFT: "Rascunho",
  SCHEDULED: "Agendada",
  SENDING: "Enviando",
  SENT: "Enviada",
  DELIVERED: "Entregue",
  READ: "Lida",
  FAILED: "Falhou",
  CANCELED: "Cancelada",
  RECEIVED: "Recebida",
};

export const STATUS_CONVERSA_TONE = {
  ABERTA: "info",
  FECHADA: "neutral",
  ARQUIVADA: "neutral",
};

export const STATUS_CONVERSA_LABEL = {
  ABERTA: "Aberta",
  FECHADA: "Fechada",
  ARQUIVADA: "Arquivada",
};
